import { FinancialMetrics, ICalculatorState } from "../Types/calculator";
import { data } from "./data"
const irrnpv = require("irr-npv")

const variables = data.variables

export function getVariable(name: string): number {
  const variable = variables.find(v => v.name === name)
  if (!variable) return 0;

  if (variable.type === "percentage") return variable.value / 100
  return variable.value
}

function getAnnualFleetMiles(state: ICalculatorState): number {
  const { fleetMiles, fleetSize, useMilesPerVehicle } = state
  return useMilesPerVehicle ? fleetMiles * fleetSize : fleetMiles
}

function getTotalTechnologyCost(state: ICalculatorState): number {
  const { fleetSize, useCalculatedCostPerUnit, costPerUnit, calculatedCostPerUnit } = state
  const totalPerVehicle = useCalculatedCostPerUnit ? calculatedCostPerUnit : costPerUnit
  return totalPerVehicle * fleetSize
}

function getTotalAnnualFees(state: ICalculatorState): number {
  const { annualFees, fleetSize } = state
  return annualFees * fleetSize
}

function getDefaultDriverData(): { driverOverheadPercent: number; driverBenefitsPercent: number } {
  return {
    driverOverheadPercent: getVariable("Driver Overhead"),
    driverBenefitsPercent: getVariable("Driver Benefits")
  }
}

function getDriverHourlyCost(state: ICalculatorState): number {
  if (state.driverTraining === "No") return 0
  const { driverOverheadPercent, driverBenefitsPercent } = getDefaultDriverData()
  return state.driverHourlyPay * (1 + driverOverheadPercent + driverBenefitsPercent)
}

function getManagerHourlyCost(state: ICalculatorState): number {
  if (state.driverCoaching === "No") return 0
  const { driverOverheadPercent, driverBenefitsPercent } = getDefaultDriverData()
  return state.managerHourlyPay * (1 + driverOverheadPercent + driverBenefitsPercent)
}

function getDefaultTrainingData(): { trainingHours: number; driversPerTruck: number; turnoverRatePercent: number, coachingHours: number } {
  return {
    trainingHours: getVariable("New driver training hours"),
    driversPerTruck: getVariable("Number of drivers per truck"),
    turnoverRatePercent: getVariable("Turnover Rate"),
    coachingHours: getVariable("Coaching hours per driver annually"),
  }
}

function getAnnualTrainingCostPerVehicle(state: ICalculatorState): number {
  const { trainingHours, driversPerTruck, turnoverRatePercent } = getDefaultTrainingData()
  const driverHourlyCost = getDriverHourlyCost(state)
  return driverHourlyCost * driversPerTruck * turnoverRatePercent * trainingHours
}

function getAnnualCoachingCostPerVehicle(state: ICalculatorState): number {
  const { driversPerTruck, coachingHours } = getDefaultTrainingData()
  const driverHourlyCost = getDriverHourlyCost(state)
  const managerHourlyCost = getManagerHourlyCost(state)
  return driverHourlyCost * driversPerTruck * coachingHours + (managerHourlyCost * coachingHours)
}

function getAnnualMaintenanceCost(state: ICalculatorState): number {
  const technologyCost = getTotalTechnologyCost(state)
  const maintenanceCostPercent = getVariable("Maintenance Rate")
  return technologyCost * maintenanceCostPercent // tech cost already accounts for fleet size
}

function getAnnualTaxSavings(state: ICalculatorState, year: number): number {
  const corporateTaxRatePercent = getVariable("Corporate Tax Rate")
  const depreciationRate = getVariable(`Depreciation${year}`)
  const totalTechnologyCost = getTotalTechnologyCost(state) // tech cost already accounts for fleet size
  return totalTechnologyCost * depreciationRate * corporateTaxRatePercent
}

function calculateEffects(crashes: number, state: ICalculatorState) {
  const { crashMitigationEffectiveness } = state
  const prevented = crashes * crashMitigationEffectiveness
  const mitigated = (crashes - prevented) * crashMitigationEffectiveness
  return { prevented, mitigated }
}

export function getDefaultNumberOfCrashes(state: ICalculatorState): { defaultFatalityCrashes: number; defaultInjuryCrashes: number; defaultPDOCrashes: number } {
  const totalFleetMiles = getAnnualFleetMiles(state)
  const defaultFatalityCrashes = parseFloat(((getVariable("Default Fatality Rate") / 100_000_000) * totalFleetMiles).toFixed(2))
  const defaultInjuryCrashes = parseFloat(((getVariable("Default Injury Rate") / 100_000_000) * totalFleetMiles).toFixed(2))
  const defaultPDOCrashes = parseFloat(((getVariable("Default PDO Rate") / 100_000_000) * totalFleetMiles).toFixed(2))
  return { defaultFatalityCrashes, defaultInjuryCrashes, defaultPDOCrashes }
}

export function getNumberOfCrashes(state: ICalculatorState): { numberOfFatalityCrashes: number; numberOfInjuryCrashes: number; numberOfPDOCrashes: number } {
  const shouldUseDefaults = state.useDefaultCrashData === "Yes" || state.useSimpleForm
  const { defaultFatalityCrashes, defaultInjuryCrashes, defaultPDOCrashes } = getDefaultNumberOfCrashes(state)
  
  return {
    numberOfFatalityCrashes: shouldUseDefaults ? defaultFatalityCrashes : state.numFatalityCrashes,
    numberOfInjuryCrashes: shouldUseDefaults ? defaultInjuryCrashes : state.numInjuryCrashes,
    numberOfPDOCrashes: shouldUseDefaults ? defaultPDOCrashes : state.numPropertyDamageCrashes
  }
}

function getFatalityEffects(state: ICalculatorState): { fatalitiesPrevented: number; fatalitiesMitigated: number } {
  const { numberOfFatalityCrashes } = getNumberOfCrashes(state)
  const { prevented: fatalitiesPrevented, mitigated: fatalitiesMitigated } = calculateEffects(numberOfFatalityCrashes, state)
  return { fatalitiesPrevented, fatalitiesMitigated }
}

function getInjuryEffects(state: ICalculatorState): { injuriesPrevented: number; injuriesMitigated: number } {
  const { numberOfInjuryCrashes } = getNumberOfCrashes(state)
  const { prevented: injuriesPrevented, mitigated: injuriesMitigated } = calculateEffects(numberOfInjuryCrashes, state)
  return { injuriesPrevented, injuriesMitigated }
}

function getPDOEffects(state: ICalculatorState): { pdosPrevented: number; } {
  const { numberOfPDOCrashes } = getNumberOfCrashes(state)
  const { prevented: pdosPrevented } = calculateEffects(numberOfPDOCrashes, state)
  return { pdosPrevented }
}

function getAnnualSafetySavings(state: ICalculatorState): number {
  const { injuryOutOfPocket, fatalOutOfPocket, pdoOutOfPocket } = state
  const { fatalitiesPrevented, fatalitiesMitigated } = getFatalityEffects(state)
  const { injuriesPrevented, injuriesMitigated } = getInjuryEffects(state)
  const { pdosPrevented } = getPDOEffects(state)

  const fatalityPreventionSavings = fatalitiesPrevented * fatalOutOfPocket
  const fatalityMitigationSavings = fatalitiesMitigated * (fatalOutOfPocket - injuryOutOfPocket)
  const injuryPreventionSavings = injuriesPrevented * injuryOutOfPocket
  const injuryMitigationSavings = injuriesMitigated * (injuryOutOfPocket - pdoOutOfPocket)
  const pdoPreventionSavings = pdosPrevented * pdoOutOfPocket

  return fatalityPreventionSavings + fatalityMitigationSavings + injuryPreventionSavings + injuryMitigationSavings + pdoPreventionSavings
}

export function getFinancialMetrics(state: ICalculatorState): FinancialMetrics {
  const { fleetSize, crashMitigationEffectiveness } = state
  const annualFees = getTotalAnnualFees(state)
  const totalTechnologyCost = getTotalTechnologyCost(state)
  const year0 = 0 - totalTechnologyCost
  const presentValue: { [key: string]: number } = {};
  const paybackChecks: { [key: string]: number } = {};
  const cashFlows: number[] = [year0];
  const costOfCapitalPercent = getVariable("Weighted Average Cost of Capital")

  let cumulativePV = year0;
  let paybackPeriod = 0, totalDollarsSaved = 0, totalDollarsSpent = -year0;

  const annualSafetySavings = getAnnualSafetySavings(state)    
  const annualMaintenanceCost = getAnnualMaintenanceCost(state);
  const annualTrainingCost = getAnnualTrainingCostPerVehicle(state) * fleetSize;
  const annualCoachCost = getAnnualCoachingCostPerVehicle(state) * fleetSize;

  for (let year = 1; year <= 5; year++) {
    const annualTaxSavings = getAnnualTaxSavings(state, year);

    const yearCashFlow = annualSafetySavings + annualTaxSavings - annualTrainingCost - annualCoachCost - annualMaintenanceCost - annualFees;
    const yearPV = yearCashFlow / Math.pow(1 + costOfCapitalPercent, year);

    presentValue[`year${year}`] = yearPV;
    cumulativePV += yearPV;
    paybackChecks[`year${year}`] = year0 + cumulativePV;
    cashFlows.push(yearCashFlow);

    if (paybackPeriod === 0 && cumulativePV > 0) paybackPeriod = year;

    totalDollarsSaved += annualSafetySavings + annualTaxSavings;
    totalDollarsSpent += (annualTrainingCost + annualCoachCost + annualMaintenanceCost + annualFees);
  }

  return {
    netPresentValue: Math.round(cumulativePV),
    irr: irrnpv.irr(cashFlows),
    paybackPeriod: paybackPeriod,
    dollarsSaved: crashMitigationEffectiveness === 0 ? 0 : totalDollarsSaved,
    dollarsSpent: totalDollarsSpent
  };
}